<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <h1 class="admin-title">📰 最新消息管理</h1>
        <p class="admin-sub">管理分類與文章</p>
      </div>
    </div>

    <!-- TAB 切換 -->
    <div class="tabs">
      <button
        :class="['tab-btn', { active: activeTab === 'articles' }]"
        @click="activeTab = 'articles'"
      >
        文章管理
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'categories' }]"
        @click="activeTab = 'categories'"
      >
        分類管理
      </button>
    </div>

    <!-- ============ 文章管理 ============ -->
    <div v-if="activeTab === 'articles'">
      <div class="admin-header">
        <div></div>
        <button class="btn-add" @click="openAddArticle">+ 新增文章</button>
      </div>

      <div class="admin-content">
        <div v-if="loadingArticles" class="loading">載入中...</div>
        <table v-else class="admin-table">
          <thead>
            <tr>
              <th>圖片</th>
              <th>標題</th>
              <th>分類</th>
              <th>日期</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in articles" :key="a.id">
              <td class="td-img">
                <img
                  v-if="a.imageUrl"
                  :src="a.imageUrl"
                  :alt="a.title"
                  class="table-img"
                />
                <span v-else class="td-emoji">{{ a.emoji }}</span>
              </td>
              <td>
                <div class="td-name">{{ a.title }}</div>
                <div class="td-caption">{{ a.excerpt }}</div>
              </td>
              <td class="td-cat">{{ a.category?.name || "未分類" }}</td>
              <td class="td-date">{{ formatDate(a.date) }}</td>
              <td>
                <span
                  :class="[
                    'status',
                    a.isPublished ? 'status-active' : 'status-inactive',
                  ]"
                >
                  {{ a.isPublished ? "已發布" : "草稿" }}
                </span>
              </td>
              <td class="td-actions">
                <button class="btn-toggle" @click="togglePublish(a)">
                  {{ a.isPublished ? "下架" : "上架" }}
                </button>
                <button class="btn-edit" @click="openEditArticle(a)">
                  編輯
                </button>
                <button class="btn-delete" @click="deleteArticle(a)">
                  刪除
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ============ 分類管理 ============ -->
    <div v-if="activeTab === 'categories'">
      <div class="admin-header">
        <div></div>
        <button class="btn-add" @click="openAddCategory">+ 新增分類</button>
      </div>

      <div class="admin-content">
        <div v-if="loadingCategories" class="loading">載入中...</div>
        <table v-else class="admin-table">
          <thead>
            <tr>
              <th>分類名稱</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in categories" :key="c.id">
              <td class="td-name">{{ c.name }}</td>
              <td class="td-actions">
                <button class="btn-edit" @click="openEditCategory(c)">
                  編輯
                </button>
                <button class="btn-delete" @click="deleteCategory(c)">
                  刪除
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ============ 文章 Modal ============ -->
    <div
      v-if="showArticleModal"
      class="modal-overlay"
      @click.self="closeArticleModal"
    >
      <div class="modal">
        <div class="modal-header">
          <h2>{{ articleEditMode ? "編輯文章" : "新增文章" }}</h2>
          <button class="modal-close" @click="closeArticleModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>標題 *</label>
            <input
              v-model="articleForm.title"
              type="text"
              placeholder="例：綠憩植研受邀參加..."
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>分類 *</label>
              <select v-model="articleForm.categoryId">
                <option :value="null" disabled>請選擇分類</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Emoji（無圖片時顯示）</label>
              <input
                v-model="articleForm.emoji"
                type="text"
                placeholder="例：🌱"
              />
            </div>
          </div>

          <div class="form-group">
            <label>摘要</label>
            <textarea
              v-model="articleForm.excerpt"
              placeholder="文章摘要..."
            ></textarea>
          </div>

          <div class="form-group">
            <label>發布日期</label>
            <input v-model="articleForm.dateInput" type="date" />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="articleForm.isPublished" />
              立即發布
            </label>
          </div>

          <div class="form-group">
            <label>封面圖片</label>
            <div class="image-upload-wrap">
              <div
                class="image-preview"
                v-if="articleForm.imageUrl || articlePreviewUrl"
              >
                <img
                  :src="articlePreviewUrl || articleForm.imageUrl"
                  alt="封面預覽"
                />
                <button class="image-remove" @click="removeArticleImage">
                  ✕
                </button>
              </div>
              <div
                v-else
                class="image-placeholder"
                @click="triggerArticleUpload"
              >
                <span>📷</span>
                <p>點擊上傳圖片</p>
                <p class="image-hint">JPG、PNG、WebP，最大 5MB</p>
              </div>
              <input
                ref="articleFileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="handleArticleImageUpload"
              />
              <button
                v-if="!articleForm.imageUrl && !articlePreviewUrl"
                class="btn-upload"
                @click="triggerArticleUpload"
              >
                {{ articleUploading ? "上傳中..." : "選擇圖片" }}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeArticleModal">取消</button>
          <button
            class="btn-save"
            @click="saveArticle"
            :disabled="articleSaving || articleUploading"
          >
            {{ articleSaving ? "儲存中..." : "儲存" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 分類 Modal ============ -->
    <div
      v-if="showCategoryModal"
      class="modal-overlay"
      @click.self="closeCategoryModal"
    >
      <div class="modal modal-sm">
        <div class="modal-header">
          <h2>{{ categoryEditMode ? "編輯分類" : "新增分類" }}</h2>
          <button class="modal-close" @click="closeCategoryModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>分類名稱 *</label>
            <input
              v-model="categoryForm.name"
              type="text"
              placeholder="例：媒體報導"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeCategoryModal">取消</button>
          <button
            class="btn-save"
            @click="saveCategory"
            :disabled="categorySaving"
          >
            {{ categorySaving ? "儲存中..." : "儲存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import axios from "axios";

const activeTab = ref("articles");

// ── 文章 ──────────────────────────────
const articles = ref([]);
const loadingArticles = ref(true);
const showArticleModal = ref(false);
const articleEditMode = ref(false);
const articleSaving = ref(false);
const articleFileInput = ref(null);
const articlePreviewUrl = ref("");
const articleUploading = ref(false);

const articleForm = reactive({
  id: null,
  title: "",
  excerpt: "",
  emoji: "🌱",
  imageUrl: "",
  categoryId: null,
  isPublished: true,
  dateInput: new Date().toISOString().slice(0, 10),
});

const resetArticleForm = () => {
  articleForm.id = null;
  articleForm.title = "";
  articleForm.excerpt = "";
  articleForm.emoji = "🌱";
  articleForm.imageUrl = "";
  articleForm.categoryId = categories.value[0]?.id ?? null;
  articleForm.isPublished = true;
  articleForm.dateInput = new Date().toISOString().slice(0, 10);
  articlePreviewUrl.value = "";
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
};

const loadArticles = async () => {
  loadingArticles.value = true;
  try {
    const res = await axios.get("/news/admin");
    articles.value = res.data;
  } catch (error) {
    console.error("載入文章失敗:", error);
  } finally {
    loadingArticles.value = false;
  }
};

const openAddArticle = () => {
  resetArticleForm();
  articleEditMode.value = false;
  showArticleModal.value = true;
};

const openEditArticle = (article) => {
  articleForm.id = article.id;
  articleForm.title = article.title;
  articleForm.excerpt = article.excerpt;
  articleForm.emoji = article.emoji;
  articleForm.imageUrl = article.imageUrl || "";
  articleForm.categoryId = article.categoryId;
  articleForm.isPublished = article.isPublished;
  articleForm.dateInput = new Date(article.date).toISOString().slice(0, 10);
  articlePreviewUrl.value = "";
  articleEditMode.value = true;
  showArticleModal.value = true;
};

const closeArticleModal = () => {
  showArticleModal.value = false;
  resetArticleForm();
};

const triggerArticleUpload = () => {
  articleFileInput.value.click();
};

const handleArticleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  articlePreviewUrl.value = URL.createObjectURL(file);
  articleUploading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post("/upload/image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    articleForm.imageUrl = response.data.url;
  } catch (error) {
    console.error("上傳失敗:", error);
    alert("圖片上傳失敗，請稍後重試");
    articlePreviewUrl.value = "";
  } finally {
    articleUploading.value = false;
  }
};

const removeArticleImage = () => {
  articleForm.imageUrl = "";
  articlePreviewUrl.value = "";
  if (articleFileInput.value) articleFileInput.value.value = "";
};

const saveArticle = async () => {
  if (!articleForm.title) {
    alert("請填寫標題");
    return;
  }
  if (!articleForm.categoryId) {
    alert("請選擇分類");
    return;
  }

  articleSaving.value = true;
  try {
    const payload = {
      title: articleForm.title,
      excerpt: articleForm.excerpt,
      emoji: articleForm.emoji,
      imageUrl: articleForm.imageUrl,
      categoryId: articleForm.categoryId,
      isPublished: articleForm.isPublished,
      date: articleForm.dateInput,
    };

    if (articleEditMode.value) {
      await axios.put(`/news/${articleForm.id}`, payload);
      alert("文章已更新！");
    } else {
      await axios.post("/news", payload);
      alert("文章已新增！");
    }
    closeArticleModal();
    await loadArticles();
  } catch (error) {
    console.error("儲存失敗:", error);
    alert("儲存失敗，請稍後重試");
  } finally {
    articleSaving.value = false;
  }
};

const togglePublish = async (article) => {
  try {
    await axios.put(`/news/${article.id}`, {
      title: article.title,
      excerpt: article.excerpt,
      emoji: article.emoji,
      imageUrl: article.imageUrl,
      categoryId: article.categoryId,
      isPublished: !article.isPublished,
      date: article.date,
    });
    await loadArticles();
  } catch (error) {
    console.error("切換狀態失敗:", error);
    alert("操作失敗，請稍後重試");
  }
};

const deleteArticle = async (article) => {
  if (!confirm(`確定要刪除「${article.title}」嗎？`)) return;
  try {
    await axios.delete(`/news/${article.id}`);
    alert("文章已刪除！");
    await loadArticles();
  } catch (error) {
    console.error("刪除失敗:", error);
    alert("刪除失敗，請稍後重試");
  }
};

// ── 分類 ──────────────────────────────
const categories = ref([]);
const loadingCategories = ref(true);
const showCategoryModal = ref(false);
const categoryEditMode = ref(false);
const categorySaving = ref(false);

const categoryForm = reactive({
  id: null,
  name: "",
});

const resetCategoryForm = () => {
  categoryForm.id = null;
  categoryForm.name = "";
};

const loadCategories = async () => {
  loadingCategories.value = true;
  try {
    const res = await axios.get("/newscategories");
    categories.value = res.data;
  } catch (error) {
    console.error("載入分類失敗:", error);
  } finally {
    loadingCategories.value = false;
  }
};

const openAddCategory = () => {
  resetCategoryForm();
  categoryEditMode.value = false;
  showCategoryModal.value = true;
};

const openEditCategory = (category) => {
  Object.assign(categoryForm, category);
  categoryEditMode.value = true;
  showCategoryModal.value = true;
};

const closeCategoryModal = () => {
  showCategoryModal.value = false;
  resetCategoryForm();
};

const saveCategory = async () => {
  if (!categoryForm.name) {
    alert("請填寫分類名稱");
    return;
  }

  categorySaving.value = true;
  try {
    if (categoryEditMode.value) {
      await axios.put(`/newscategories/${categoryForm.id}`, {
        name: categoryForm.name,
      });
      alert("分類已更新！");
    } else {
      await axios.post("/newscategories", { name: categoryForm.name });
      alert("分類已新增！");
    }
    closeCategoryModal();
    await loadCategories();
  } catch (error) {
    console.error("儲存失敗:", error);
    alert(error.response?.data?.message || "儲存失敗，請稍後重試");
  } finally {
    categorySaving.value = false;
  }
};

const deleteCategory = async (category) => {
  if (!confirm(`確定要刪除「${category.name}」嗎？`)) return;
  try {
    await axios.delete(`/newscategories/${category.id}`);
    alert("分類已刪除！");
    await loadCategories();
  } catch (error) {
    console.error("刪除失敗:", error);
    alert(error.response?.data?.message || "刪除失敗，請稍後重試");
  }
};

onMounted(async () => {
  await loadCategories();
  await loadArticles();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.admin-page {
  padding: 40px;
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.admin-title {
  font-size: 1.8em;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.admin-sub {
  color: #6b7f68;
  font-size: 14px;
}
.btn-add {
  padding: 12px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}
.btn-add:hover {
  background: #2d5016;
}

/* TABS */
.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #eee;
}
.tab-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 600;
  color: #9baa97;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.tab-btn.active {
  color: #4e7b4f;
  border-bottom-color: #4e7b4f;
}

.admin-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow-x: auto;
}
.admin-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}
.admin-table th {
  background: #f5f7fa;
  padding: 14px 16px;
  text-align: left;
  font-size: 13px;
  color: #6b7f68;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
}
.admin-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.admin-table tr:last-child td {
  border-bottom: none;
}
.admin-table tr:hover td {
  background: #f9fafb;
}
.td-img {
  width: 70px;
  text-align: center;
}
.table-img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}
.td-emoji {
  font-size: 28px;
}
.td-name {
  font-weight: 600;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.td-caption {
  font-size: 12px;
  color: #9baa97;
}
.td-cat {
  font-size: 13px;
  color: #4e7b4f;
  white-space: nowrap;
}
.td-date {
  font-size: 13px;
  color: #6b7f68;
  white-space: nowrap;
}
.status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}
.status-active {
  background: #eaf2e0;
  color: #4e7b4f;
}
.status-inactive {
  background: #f5f7fa;
  color: #9baa97;
}
.td-actions {
  display: flex;
  gap: 8px;
  white-space: nowrap;
}
.btn-toggle {
  padding: 6px 14px;
  background: #f5f7fa;
  color: #4e7b4f;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-toggle:hover {
  background: #eaf2e0;
}
.btn-edit {
  padding: 6px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-edit:hover {
  background: #5a6fd6;
}
.btn-delete {
  padding: 6px 16px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-delete:hover {
  background: #ff5252;
}
.loading {
  padding: 60px;
  text-align: center;
  color: #9baa97;
}

/* MODAL */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: white;
  border-radius: 16px;
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.modal-sm {
  width: 420px;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}
.modal-header h2 {
  font-size: 18px;
  color: #1c2b1a;
}
.modal-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #9baa97;
}
.modal-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #3d4f3a;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px !important;
  font-weight: 500 !important;
}
.form-group input[type="text"],
.form-group input[type="date"],
.form-group select,
.form-group textarea {
  padding: 10px 14px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
}
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4e7b4f;
}
.form-group textarea {
  min-height: 90px;
  resize: vertical;
}
.image-upload-wrap {
  border: 2px dashed #ddd;
  border-radius: 12px;
  overflow: hidden;
}
.image-preview {
  position: relative;
  width: 100%;
  max-height: 200px;
}
.image-preview img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}
.image-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 14px;
}
.image-placeholder {
  padding: 30px 20px;
  text-align: center;
  cursor: pointer;
}
.image-placeholder:hover {
  background: #f9fafb;
}
.image-placeholder span {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}
.image-placeholder p {
  font-size: 14px;
  color: #6b7f68;
  margin-bottom: 4px;
}
.image-hint {
  font-size: 12px !important;
  color: #9baa97 !important;
}
.btn-upload {
  width: 100%;
  padding: 10px;
  background: #f5f7fa;
  color: #4e7b4f;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
}
.btn-upload:hover {
  background: #eaf2e0;
}
.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: white;
}
.btn-cancel {
  padding: 10px 24px;
  background: #f5f7fa;
  color: #6b7f68;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.btn-save {
  padding: 10px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}
.btn-save:hover:not(:disabled) {
  background: #2d5016;
}
.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
